import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import colors from "../../../theme/colors";
import { downloadSubmissionPdf, submissionPdfBlob } from "../submissionPdf";

/**
 * The real generated PDF, inline.
 *
 * It's rendered client-side with jsPDF (there is no render-to-PDF route on the
 * backend), so this previews the exact bytes the Download button writes — not a
 * mock-up of them. The blob URL is created when this tab first mounts and
 * revoked on unmount; leaving it alive would leak one object URL per row the
 * user expands.
 */
const SubmissionPdfTab = ({ row }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!row) return undefined;
    // Async because the letterhead logo has to be read before the document can
    // be drawn. `cancelled` covers the tab being closed mid-render: revoking a
    // URL we never set would leak the blob instead.
    let objectUrl;
    let cancelled = false;

    (async () => {
      try {
        const blob = await submissionPdfBlob(row);
        if (cancelled) return;
        if (blob) {
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [row]);

  if (failed || (!url && row)) {
    return (
      <Flx
        column
        centerX
        centerY
        gap={1.5}
        sx={{ py: 6, background: colors.slate50, borderRadius: "10px" }}
      >
        <Txt component="div" sx={{ fontSize: 13, color: colors.slate500 }}>
          {failed
            ? "This submission's PDF couldn't be generated."
            : "Generating…"}
        </Txt>
        {failed && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => downloadSubmissionPdf(row)}
          >
            Try downloading it
          </Button>
        )}
      </Flx>
    );
  }

  return (
    <Box
      sx={{
        background: colors.slate100,
        border: `1px solid ${colors.slate200}`,
        borderRadius: "10px",
        overflow: "hidden",
        // Full screen height, less the app chrome above the expanded row — a
        // letter page is tall, and any fixed cap (this used to be 900px) wasted
        // the bottom third of a large monitor.
        height: { xs: 520, md: "calc(100vh - 96px)" },
      }}
    >
      {/* `title` because an untitled iframe is unlabelled to a screen reader. */}
      <Box
        component="iframe"
        title="Submission PDF"
        src={url}
        sx={{ width: "100%", height: "100%", border: "none", display: "block" }}
      />
    </Box>
  );
};

export default SubmissionPdfTab;
